<template>
  <v-container
    grid-list-md
    text-xs-center
    white
    id="settings"
    role="dialog"
    aria-labelledby="Settings menu"
    :aria-describedby="`${termForBulb.capitalize()} settings menu`"
  >
    <v-layout wrap>
      <v-flex
        xs8
        style="display: flex; align-items: center"
      >
        <v-slider
          id="volume_slider"
          :disabled="mobileIOS"
          aria-label="volume slider"
          :prepend-icon="volume_icon"
          @click:prepend="toggleVolume"
          v-model="bulb.config.volume"
          @input="setVolume(volume)"
          hide-details
          class="mt-0"
          ref="volumeSlider"
        ></v-slider>
      </v-flex>
      <v-flex
        xs4
        style="display: flex; align-items: center"
      >
        <playback-speed
          :speeds="playbackSpeeds"
          id="all"
        ></playback-speed>
      </v-flex>
    </v-layout>
    <v-layout
      v-if="showAdditionalSettings"
      row
      wrap
    >
      <v-flex xs12>
        <v-layout
          row
          wrap
          justify-space-around
          align-center
        >
          <v-flex
            xs4
            class="display-times text-sm-left"
          >
            Segments
          </v-flex>
          <v-flex
            xs4
            class="text-sm-left"
          >
            Video
          </v-flex>
          <v-flex
            xs4
            class="text-sm-left bulb-caption-settings-dialog"
          >
          Captions
        </v-flex>
        </v-layout>
        <v-layout
          row
          wrap
          justify-space-around
          align-center
          v-for="(bulbVideo, bulbVideoIndex) in filterdBulbVideos"
          :key="bulbVideo.id"
        >
          <v-flex
            xs4
            class="display-times text-sm-left"
            :aria-label="'video ' + bulbVideoIndex"
          >
            {{ formatedDuration(bulbVideo.displayStart) }} -
            {{ formatedDuration(bulbVideo.displayEnd) }}
          </v-flex>
          <v-flex xs4>
            <b-form-select
              aria-label="Select Stream Source/Quality"
              box
              class="full-width full-height"
              v-model="bulbStreamMap[bulbVideoIndex]"
              @change="setQualityLevel()"
            >
              <optgroup
                v-for="(bulbVideoStream, videoStreamIndex ) in bulbVideo.bulbVideoStreams"
                :key="videoStreamIndex"
                :label="'Source ' + (videoStreamIndex + 1)"
              >
                <option
                  v-for="(s, i) in formatStreams(
                    bulbVideoStream,
                    videoStreamIndex,
                    bulbVideoIndex,
                  )"
                  :key="i"
                  :value="s.value"
                >
                  {{ s.text }}
                </option>
              </optgroup>
            </b-form-select>
          </v-flex>
          <v-flex
            xs4
            class="bulb-caption-settings-dialog"
          >
            <b-form-select
              aria-label="Select Captions"
              box
              class="full-width full-height"
              v-model="bulbVideo.captionId"
              :disabled="captionsDisabled"
            >
              <option
                key="disable-caption"
                :disable="bulbVideo.captionId === -1"
                value="-1"
              >
                None
              </option>
              <optgroup
                v-for="(captions, captionsType) in captionGroups[bulbVideo.id]"
                :key="captionsType"
                :label="capitalizeCaptionGroup(captionsType)"
              >
                <option
                  v-for="(caption, i) in formatCaptions(captions)"
                  :key="i"
                  :disable="bulbVideo.captionId === caption.id"
                  :value="caption.value"
                >
                  {{ caption.text }}
                </option>
              </optgroup>
            </b-form-select>
          </v-flex>
        </v-layout>
        <v-layout v-if="canHideCaptions && showHideCaptionsToggle">
          <v-checkbox
            hide-details
            class="display-times mb-1 mt-0"
            v-model="hideCaptionsEnableTranscript"
            label="Show captions only in transcript"
            @change="handleHideCaptionsEnableTranscriptChange"
          >
          </v-checkbox>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout
      v-if="showPlaybackToggle"
      id="instructorPlaybackMode"
      row
      wrap
      justify-space-around
      align-center
      aria-labeledby="playbackToggleLabel"
      aria-describedby="playbackModeToggle"
    >
      <v-flex
        xs6
        style="display: flex; align-items: center"
      >
        <v-flex
          id="playbackToggleLabel"
          class="text-sm-left"
          >Playback Mode:</v-flex
        >
      </v-flex>
      <v-flex style="display: flex; align-items: center">
        <b-form-select
          id="playbackModeToggle"
          :aria-label="`Select ${termForBulb.capitalize()} Playback Mode`"
          box
          class="full-width full-height"
          v-model="playbackMode"
          @change="updatePlaybackMode"
        >
          <option
            value="learner"
            :aria-label="`Preview ${termForBulb} as a learner`"
          >
            Learner
          </option>
          <option
            value="instructor"
            :aria-label="`Preview ${termForBulb} as an instructor`"
          >
            Instructor
          </option>
        </b-form-select>
      </v-flex>
    </v-layout>
    <v-layout
      v-if="showPlayerControlsDisplayOptions && videoScrubBarDisplay !== 'none'"
      id="playerControlsDisplayOptions"
      row
      wrap
      justify-space-around
      align-center
      aria-labeledby="playerControlsDisplayOptionsLabel"
      aria-describedby="playerControlsDisplayOptionsToggle"
    >
      <v-flex
        xs6
        style="display: flex; align-items: center"
      >
        <v-flex
          id="playerControlsDisplayOptionsLabel"
          class="text-sm-left"
        >
          Controls Display:
        </v-flex>
      </v-flex>
      <v-flex style="display: flex; align-items: center">
        <b-form-select
          id="playerControlsDisplayOptionsToggle"
          aria-label="Select Player controls display options"
          box
          class="full-width full-height"
          v-model="playerControlsDisplayOption"
          @change="updatePlayerControlsDisplayOption"
        >
          <option
            value="Always"
            aria-label="Player controls are always shown"
          >
            Always
          </option>
          <option
            value="Fade"
            aria-label="Player controls fade in and out"
          >
            Fade
          </option>
        </b-form-select>
      </v-flex>
    </v-layout>
    <span
      class="screen-reader-tag"
      aria-label="close settings menu"
      @keyup.enter="setSettingsMenu(false)"
      @click="setSettingsMenu(false)"
      role="button"
      tabindex="0"
      >close settings Menu</span
    >
  </v-container>
</template>

<script>
/* eslint-disable */

// Plugins / Libraries:
import _ from 'lodash';
import screenfull from 'screenfull';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

// Custom components:
import EventBus from '@/buses/EventBus';
import PlaybackSpeed from './PlaybackSpeed';

export default {
  name: 'settings',
  data() {
    return {
      action: 'settings',
      bulbStreamMap: [],
      hideCaptionsEnableTranscript: false,
      playbackMode: 'instructor',
      playerControlsDisplayOption: 'Always',
      selected: false,
      speeds: [
        { text: '.25x Speed', value: 0.25, css: 'quarter' },
        { text: '.50x Speed', value: 0.5, css: 'half' },
        { text: '1x Speed', value: 1, css: 'normal' },
        { text: '1.25x Speed', value: 1.25, css: 'one-quarter' },
        { text: '1.50x Speed', value: 1.5, css: 'one-half' },
        { text: '2x Speed', value: 2, css: 'double' },
      ],
      videoScrubBarDisplay: '',
      volume_icon: 'volume_down',
    };
  },
  components: {
    PlaybackSpeed,
  },
  computed: {
    ...mapGetters('player', [
      'bulb',
      'bulbInitialized',
      'displayTime',
      'fullScreenIcon',
      'isBroadcastStudent',
      'mobile',
      'mobileIOS',
      'settingsMenu',
      'volume',
      'termForBulb',
    ]),
    ...mapState('player', [
      'bulbVideoQualityLevels',
      'formatedDuration',
      'isFullScreen',
      'isLiveSession',
      'queryParams',
      'screenfull',
      'user',
    ]),
    aria() {
      return {
        fullscreen: `Toggle Fullscreen ${this.isFullScreen ? 'Off' : 'On'}`,
      };
    },
    filterdBulbVideos() {
      return this.bulb.bulbVideos.filter((bulbVideo) => {
        return (
          bulbVideo.bulbVideoStreams.length > 0 ||
          bulbVideo.bulbVideoStreams.length > 1 ||
          bulbVideo.bulbVideoCaptions.length > 0 ||
          bulbVideo.bulbVideoStreams.type === 'm3u8'
        );
      });
    },
    captionGroups() {
      const groups = {};
      if (!this.bulb.bulbVideos.length) return;
      this.bulb.bulbVideos.forEach((bv) => {
        groups[bv.id] = {};
        bv.bulbVideoCaptions.forEach((bvc) => {
          if (_.has(groups, `${bv.id}.${bvc.type}`)) {
            groups[bv.id][bvc.type].push(bvc);
          } else {
            groups[bv.id][bvc.type] = [bvc];
          }
        });
      });
      return groups;
    },
    canHideCaptions() {
      return _.get(
        this,
        'bulb.masterTeacher.school.permissionKeys',
        [],
      ).includes('hideCaptionsEnableTranscript');
    },
    captionsDisabled() {
      return _.get(this.bulb, 'config.captions_disabled', 0) === 1;
    },
    playbackSpeeds() {
      // Live session only sees x1 speed
      if (this.isLiveSession) return this.normalSpeed;
      // if fast_playback = 1 or isnt set - return all speeds
      if (_.get(this.bulb, 'config.fast_playback', 1) === 1) return this.speeds;
      // otherwise return just slow speeds
      return this.speedsSlow;
    },
    showAdditionalSettings() {
      let showSettings = false;
      this.bulb.bulbVideos.forEach((bv) => {
        if (
          bv.bulbVideoStreams.length > 1 ||
          bv.bulbVideoCaptions.length > 0 ||
          _.find(bv.bulbVideoStreams, { type: 'm3u8' })
        ) {
          showSettings = true;
        }
      });
      return showSettings;
    },
    showHideCaptionsToggle() {
      return (
        this.bulb.hasCaptions &&
        !_.get(this, 'bulb.config.captions_disabled') &&
        !_.get(this, 'bulb.config.enable_transcript')
      );
    },
    showPlaybackToggle() {
      // Playback toggles only display for techers
      if (this.user.userType !== 'teacher') return false;
      // If bulb created by a teacher in school 43 don't display playback toggle
      if (
        _.get(this, 'bulb.teachers[0].school.id') === 43 ||
        this?.bulb?.teachers?.[0]?.school?.permissionKeys?.includes(
          'enableYoutubeFriendlyUi',
        )
      )
        return false;
      // If userId = 1351942 don't display playback toggle
      if (this.user.id === 1351942) return false;
      return true;
    },
    showPlayerControlsDisplayOptions() {
      if (this.mobile) return false;
      if (this.isLiveSession) return false;
      if (this.isBroadcastStudent) return false;
      return true;
    },
    speedsFast() {
      return _.filter(this.speeds, (s) => s.value >= 1);
    },
    speedsSlow() {
      return _.filter(this.speeds, (s) => s.value <= 1);
    },
    normalSpeed() {
      return _.filter(this.speeds, (s) => s.value === 1);
    },
  },
  methods: {
    ...mapActions('player', ['togglePlaybackMode']),
    ...mapMutations('player', [
      'setHideCaptionsEnableTranscript',
      'setIsFullScreen',
      'setPlaybackModeType',
      'setPlayerControlsDisplayType',
      'setScreenfull',
      'setSettingsMenu',
      'setSelectedStreamQuality',
    ]),
    capitalizeCaptionGroup(captionType = '') {
      const lowercaseCaptionType = captionType.toLowerCase();
      if (
        [
          '3cmedia',
          'threecmedia',
          'techconnect',
          '3c media'
        ].includes(lowercaseCaptionType)
      ) {
        return '3C Media';
      }

      if (lowercaseCaptionType === 'vimeoembed') return 'Vimeo';
      if (lowercaseCaptionType === 'canvasstudio') return 'Canvas Studio';
      
      return _.capitalize(captionType);
    },
    formatStreams(stream, index, videoIndex) {
      // convert bulb streams into select input values: [{text:stream.name, value: {index of stream, streamQuality}}]
      const parsedStreams = [];
      parsedStreams.push({
        text: `${
          stream.type === 'm3u8'
            ? 'Auto'
            : this.formatStreamSourceType(stream.type)
        }`,
        value: {
          index,
          quality: 'auto',
        },
      });
      if (this.bulbVideoQualityLevels.length) {
        let streamQualities;
        if (
          this.bulbVideoQualityLevels[index]
          && this.bulbVideoQualityLevels[index].qualityLevels
        ) {
          streamQualities = this.bulbVideoQualityLevels[index].qualityLevels;
        }
        if (streamQualities && stream.type === 'm3u8') {
          for (const qualityLevel of streamQualities) {
            parsedStreams.push({
              text: `${qualityLevel.height}p`,
              value: {
                index,
                quality: qualityLevel.height,
              },
            });
          }
        }
      }
      return parsedStreams;
    },
    formatCaptions(captions = []) {
      // format bulb captions into select input values: [{text:caption.title, value:}]
      const parsed_captions = [];
      captions.forEach((caption, i) => {
        parsed_captions.push({ text: caption.label, value: caption.id });
      });
      return parsed_captions;
    },
    handleHideCaptionsEnableTranscriptChange() {
      this.setHideCaptionsEnableTranscript(this.hideCaptionsEnableTranscript);
    },
    toggleVolume() {
      const volume = this.volume > 0 ? 0 : 70;
      this.volume_icon = volume === 0 ? 'volume_mute' : 'volume_up';
      this.$set(this.bulb.config, 'volume', volume);
    },
    setQualityLevel() {
      this.bulbStreamMap.forEach((bs, i) => {
        this.bulb.bulbVideos[i].streamIndex = bs.index;
        this.setSelectedStreamQuality({ streamIndex: i, bs });
      });
    },
    setVolume(volume) {
      if (volume > 50) {
        this.volume_icon = 'volume_up';
      } else if (volume > 0) {
        this.volume_icon = 'volume_down';
      } else {
        this.volume_icon = 'volume_mute';
      }
      if (volume < 6) {
        this.bulb.config.volume = 0;
      }
    },
    formatStreamSourceType(type) {
      if (type === 'mp4') return 'MP4';
      if (type === 'youtube') return 'YouTube';
      else return _.capitalize(type);
    },
    fullScreen() {
      // toggle full screen
      this.screenfull.toggle();
      this.setSettingsMenu(false);
    },
    updatePlaybackMode() {
      this.setPlaybackModeType(this.playbackMode);
      this.togglePlaybackMode({
        originalValues: this.bulb.originalValues,
        playbackModeType: this.playbackMode,
      }).then(() => {
        // Grab required interaction to rewind to that point - only happens a few times so not creating a computed
        const i = this.$store.getters['interactions/requiredInteractionIndex'];
        const interactions = this.$store.getters['interactions/interactions'];
        if (interactions.length > 0 && i >= 0 && this.bulbInitialized) {
          if (interactions[i].displayInTime < this.displayTime) {
            EventBus.$emit('requestAction', {
              key: 'seek',
              seekTime: interactions[i].displayInTime,
              bypassGates: ['notWithinBounds', 'alreadySeeking'],
            });
          }
        }
      });
    },
    updatePlayerControlsDisplayOption() {
      this.setPlayerControlsDisplayType(this.playerControlsDisplayOption);
      this.setSettingsMenu(false);
    },
  },
  watch: {
    bulbInitialized(newValue, oldValue) {
      // This handles the case if the school has injected css that explicitly hides our player video-scrub-bar.
      // If that is the case, then we don't show the controls display option in the settings dialog.
      if (newValue) {
        this.videoScrubBarDisplay = getComputedStyle(
          document.getElementById('video-scrub-bar'),
        ).display;
      }
    },
  },
  mounted() {
    // Testing fullscreen capability on mobile devices. Currently -- the fullscreen API isn't fully supported with
    // Safari iOS on *iPhone*. However iPad it is supported.
    this.setScreenfull(screenfull);
    if (this.screenfull.isEnabled) {
      this.screenfull.on('change', () => {
        this.setIsFullScreen(screenfull.isFullscreen);
      });
    }
    if (
      _.get(this.bulb, 'config.captions_disabled', 0) === 1 &&
      _.get(this.bulb, 'config.enable_transcript', 1) === 0
    ) {
      for (const bulbVideo of this.bulb.bulbVideos) {
        bulbVideo.captionId = -1;
      }
    }
    if (
      _.get(this.bulb, 'teachers[0].school.id') === 43 ||
      this.userType === 'anonymous' ||
      this.user.id === 1351942 ||
      this?.bulb?.teachers?.[0]?.school?.permissionKeys?.includes(
        'enableYoutubeFriendlyUi',
      )
    ) {
      this.togglePlaybackMode({
        originalValues: this.bulb.originalValues,
        playbackModeType: 'instructor',
      });
    }
    this.bulbStreamMap = Array(this.bulb.bulbVideos.length).fill({
      index: 0,
      quality: 'auto',
    });
  },
};
</script>

<style>
.setting_btn i {
  font-size: 3em;
  width: 1em;
}
#fullscreen:hover {
  cursor: pointer;
}
.time_span {
  display: inline-block;
  /*padding: 5px 0;*/
}
.volume-toggle {
  cursor: pointer;
}
.display-times {
  font-size: 15px;
}
.settings-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto !important;
}
.settings-buttons > *:not(:first-child) {
  margin-left: 5px;
  margin-right: 0;
}
.slider {
  height: auto !important;
  margin-left: 0 !important;
}
.input-group--slider {
  padding-top: 0;
  padding: 4px 16px 0 0 !important;
}
.input-group--slider .input-group__details {
  min-height: 0;
}
.input-group--slider .input-group__prepend-icon {
  width: 48px;
}
.settings-menu.v-dialog {
  position: absolute;
  right: 14px;
  bottom: 55px;
  margin: 0px;
  width: 360px;
}
#hideCaptionsShowTranscriptLayout label {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
}
</style>
